import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCGrid, RCCHookToast, RCCInput, RCCRefModalProps, RCCSwitch, RCCServicesApi } from "react-common-components";
import { useFieldArray, useForm } from "react-hook-form";
import Tabs from "src/components/Tabs";
import Title from "src/components/Title";
import Modal from "src/components/Modal";
import Audit from "src/components/Audit";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import { v4 } from "uuid";
import ModalSide from "src/components/Modal/styles";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { ICanvasModels, IComponentCanvasModelsModals, IRefCanvasModelsModals } from "./interface";
import addAndChangeSchema from "./schemas";
import Quadrants from "./Components/Quadrant";


const Modals: React.ForwardRefRenderFunction<IRefCanvasModelsModals, IComponentCanvasModelsModals> = ({
	paginationRef
}, ref) => {
	const { translate } = useTranslation();
	const {addToast} = RCCHookToast();
	const {confirm} = useConfirmModal();
	const [selectedCanvasModels, setSelectedCanvasModels] = useState<ICanvasModels>({} as ICanvasModels);

	const actionButtonDelete = (row: ICanvasModels) => {
		confirm(
			translate("Exclusão do modelo"),
			`${translate("Deseja realmente excluir o modelo")}: ${row.caa001Descricao}?`,
			async () => {
				await RCCServicesApi.deleteRequest({
					method: "CanvasModels/Delete",
					urlParameters: {
						id: row.caa001Id
					}
				});
	
				addToast({
					color: "green",
					description: translate("Modelo excluído com sucesso"),
					title: translate("Sucesso")
				});

				paginationRef.current?.reload();
			},
			(err) => {
				standardErrorMessages(err, "Erro ao excluir modelo", addToast);
			},
			true
		);
	};
	
	// Modal Add/Change
	const [isAdd, setIsAdd] = useState(false);
	const modalAddOrChange = useRef<RCCRefModalProps>(null);
	const { 
		handleSubmit : handleSubmitAddOrChange, 
		register : registerAddOrChange,
		control: controlAddOrChange, 
		reset : resetAddOrChange, 
		setError : setErrorAddOrChange, 
		formState: { errors : errorsAddOrChange }, 
		clearErrors: clearErrorsAddOrChange,
		setValue,
		watch
	} = useForm<ICanvasModels>();
	
	const { 
		fields, 
		remove,
		prepend,
		append
	} = useFieldArray({
		control: controlAddOrChange,
		name: "catba002"
	});

	const watchFieldArray = watch("catba002");
	const controlledFields = fields.map((field, index) => ({
		...field,
		...watchFieldArray && watchFieldArray[index]
	}));

	const submitAddOrChange = async (data: any) => {
		try{
			clearErrorsAddOrChange();
			modalAddOrChange.current?.setLoadingButton();
			await addAndChangeSchema(translate).validate(data, {
				abortEarly: false
			});

			if(isAdd){
				await RCCServicesApi.postRequest({
					method: "CanvasModels/Insert",
					body: {
						...data,
						caa001Situacao: data.caa001Situacao ? "S" : "N"
					}
				});
			}else{
				await RCCServicesApi.putRequest({
					method: "CanvasModels/Update",
					body: {
						...data,
						caa001Id: selectedCanvasModels.caa001Id,
						caa001Situacao: data.caa001Situacao ? "S" : "N"
					}
				});
			}

			addToast({
				color: "green",
				description: isAdd ? translate("Modelo adicionado com sucesso") : translate("Modelo alterado com sucesso"),
				title: translate("Sucesso")
			});

			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();
			paginationRef.current?.reload();

		}catch(err){
			modalAddOrChange.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorAddOrChange)){
				addToast({
					color: "yellow",
					description: translate("Verifique os campos e tente novamente"),
					title: translate("Atenção")
				});
				return;
			}

			standardErrorMessages(err, isAdd ? translate("Erro ao adicionar modelo") : translate("Erro ao alterar modelo"), addToast);
		}
	};
	const actionButtonAdd = () => {
		resetAddOrChange(); 
		setSelectedCanvasModels({} as ICanvasModels); 
		setIsAdd(true); 
		modalAddOrChange.current?.openModal();
	};
	const actionButtonChange = (row: ICanvasModels) => {
		resetAddOrChange(); 
		setIsAdd(false); 
		setSelectedCanvasModels(row);
		getCanvasModels(row.caa001Id);
		modalAddOrChange.current?.openModal(); 
	};

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange,
		actionButtonDelete
	}));
    
	const getCanvasModels = async(idModel: number) => {

		const dataController = await RCCServicesApi.getRequest({
			method: "CanvasModels/Select",
			urlParameters: {id: idModel}
		});
	
		const quadrants = dataController.data.catba002.map((x:any) => ({
			id: v4(),
			caa002Id: x.caa002Id,
			caa002Codigo: x.caa002Codigo,
			caa002Descricao: x.caa002Descricao,
			caa002Explicacao: x.caa002Explicacao,
			caa002Cor: x.caa002Cor,
			collapseExplanation: false,
			disabledCode: true
		}));

		append(quadrants);
	};

	return (
		<>
			{/* Modal ADD/CHANGE */}
			<Modal
				ref={modalAddOrChange}
				header={{
					title: isAdd ? translate("Adicionar Modelo CANVAS") : translate("Alterar Modelo CANVAS"),
					icon: {
						name: "building"
					}
				}}
				onSubmit={handleSubmitAddOrChange(submitAddOrChange)}
				styleInline={ModalSide}
			>
				<Tabs
					name="canvas-models-tab"
					tabInit={0}
					options={[{
						label: translate("Dados"),
						content: (
							<RCCGrid
								container
								item
								direction="column"
								alignItems="center"
							>
								<RCCGrid
									container
									item
									xs={12}
									sm={10}
									alignItems="center"
									justify="space-between"
								>
									<Title color="purple" size="md">
						    			{translate("Dados Gerais")}
									</Title>
									<Audit data={selectedCanvasModels} type="pop"/>
									<hr />
								</RCCGrid>
								<RCCGrid
									container
									item
									xs={12}
									sm={10}
								>
									<input
										name="caa001Id" 
										type="text"
										value={selectedCanvasModels.caa001Id}
										style={{ display: "none" }} 
										readOnly
									/>
									<RCCInput 
										register={registerAddOrChange}
										name="caa001Codigo"
										label={translate("Código")}
										maxLength={60}
										defaultValue={selectedCanvasModels.caa001Codigo}
										error={errorsAddOrChange.caa001Codigo}
										disabled={!isAdd}
										required
									/>
								</RCCGrid>
								<RCCGrid
									container
									item
									xs={12}
									sm={10}
								>
									<RCCInput 
										register={registerAddOrChange}
										name="caa001Descricao"
										label={translate("Descrição")}
										maxLength={60}
										defaultValue={selectedCanvasModels.caa001Descricao}
										error={errorsAddOrChange.caa001Descricao}
										required
									/>
								</RCCGrid>
								<RCCGrid
									container
									item
									xs={12}
									sm={10}
								>
									<RCCGrid
										container
										item
										xs={12}
										sm={6}
										md={4}
										lg={3}
									>
										<RCCSwitch 
											name="caa001Situacao" 
											label={translate("Ativo")} 
											register={registerAddOrChange} 
											defaultChecked={selectedCanvasModels.caa001Situacao !== "N"}
										/>
									</RCCGrid>
								</RCCGrid>
								
							</RCCGrid>
						)
					},
					{
						label: translate("Quadrantes"),
						content: (
							<RCCGrid
								container
								item
								direction="column"
								alignItems="center">
								<RCCGrid
									container
									item
									xs={12}
									sm={10}
								>
									<Quadrants
										source = {controlledFields}
										setValue={setValue}
										register={registerAddOrChange}
										control={controlAddOrChange}
										remove={remove}
										prepend={prepend}
										errors={errorsAddOrChange}
										clearErrors={clearErrorsAddOrChange}
									/>
								</RCCGrid>
							</RCCGrid>
						)
					}
					]}>
				</Tabs>

				
			</Modal>
		</>
	);
};

export default forwardRef(Modals);
